import { mediaSharing } from 'util/screen-sharing/mediaSharing';
import { type Source } from 'util/screen-sharing/streamManager';
import { useEffect, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@web-chat-core/ui-kit/core/Button';
import Typography from '@web-chat-core/ui-kit/core/Typography';
import { useModal } from 'useCase/modal/useModal';
import { HOT_KEY, Icon, LoadingDots, useHotKey } from '@web-chat-core/ui-kit';
import { CustomModalLayout } from './CustomModalLayout';
import {
    SourceItemsWrapper,
    SourceItemImage,
    SourceItem,
    SourceItemAppIcon,
    FooterWrapper,
    SourceItemImageStub,
    ScreenNameWrapper,
} from './ScreenShareModal.view';

export const ScreenShareModal = () => {
    const { t } = useTranslation();
    const { closeModal } = useModal();
    const [isLoading, startTransition] = useTransition();
    const [sources, setSources] = useState<Source[] | null>(null);
    const [selectedSource, setSelectedSource] = useState<Source | null>(null);
    const [isShareScreenPermissionNotGranted, setIsShareScreenPermissionNotGranted] = useState<boolean>(false);

    const closeShareScreenModal = () => {
        mediaSharing.rejectGetDisplayMediaPromise('Modal closed');
        closeModal();
    };

    const handleSourceItemClick = async (source: Source) => {
        if (mediaSharing.streamManager) {
            mediaSharing.streamManager.selectedSource = source;
            setSelectedSource(source);
        }
    };

    const handleStartSharingClick = async () => {
        if (mediaSharing.streamManager) {
            const stream = await mediaSharing.streamManager.getStream();
            mediaSharing.resolveGetDisplayMediaPromise(stream);
        }
        closeModal();
    };

    const handleCloseModalClick = () => {
        closeShareScreenModal();
    };

    const handleEscKeyDown = () => {
        closeShareScreenModal();
    };

    useHotKey(HOT_KEY.ESC, handleEscKeyDown, { handlerName: 'close-modal' });

    useEffect(() => {
        startTransition(async () => {
            try {
                const sources = await window.electronApi.ipcRenderer.getScreenShareSources();
                setSources(sources.sort((a, b) => a.id.localeCompare(b.id)));
            } catch (e) {
                const permissionStatus = await window.electronApi.ipcRenderer.checkShareScreenPermission();
                if (permissionStatus !== 'granted') {
                    setIsShareScreenPermissionNotGranted(true);
                } else {
                    console.error('Error while getting screen sources', e);
                }
            }
        });
    }, []);

    const renderModalBody = () => {
        if (isShareScreenPermissionNotGranted) {
            return (
                <SourceItemsWrapper>
                    <Typography variant={'subtitle4'}>{t('web_calls_screen_share_failed')}</Typography>
                </SourceItemsWrapper>
            );
        }

        return (
            <SourceItemsWrapper>
                {isLoading && <LoadingDots />}
                {sources?.map((source: Source) => {
                    // appIcon и thumbnail представляет собой строку вида 'data:image/png;base64,blablabla',
                    // либо 'data:image/png;base64,' - если иконки нет
                    const checkImageExist = (base64Image: string | undefined) => {
                        return (
                            base64Image && base64Image.split(',')[1] !== '' && base64Image.split(',')[1] !== undefined
                        );
                    };

                    const isAppIconExist = checkImageExist(source.appIcon);
                    const isThumbnailExist = checkImageExist(source.thumbnail);

                    return (
                        <SourceItem
                            key={source.id}
                            onClick={() => {
                                handleSourceItemClick(source);
                            }}
                        >
                            {isThumbnailExist ? (
                                <SourceItemImage
                                    $selected={selectedSource?.id === source.id}
                                    src={source.thumbnail}
                                    alt={source.name}
                                    draggable="false"
                                />
                            ) : (
                                <SourceItemImageStub $selected={selectedSource?.id === source.id}>
                                    <Icon icon="icImgs32" width={32} height={32} />
                                </SourceItemImageStub>
                            )}

                            <ScreenNameWrapper>
                                {isAppIconExist && (
                                    <SourceItemAppIcon
                                        src={source.appIcon}
                                        alt={source.name}
                                        width={24}
                                        height={24}
                                        draggable="false"
                                    />
                                )}
                                <Typography textOverflowDisabled={true} variant={'subtitle2'}>
                                    {source.name}
                                </Typography>
                            </ScreenNameWrapper>
                        </SourceItem>
                    );
                })}
            </SourceItemsWrapper>
        );
    };

    const renderModalFooter = () => {
        if (isShareScreenPermissionNotGranted) {
            return (
                <FooterWrapper>
                    <Button onClick={handleCloseModalClick}>
                        {t('web_videoconference_AI_Assistant_onbording_button')}
                    </Button>
                </FooterWrapper>
            );
        }

        return (
            <FooterWrapper>
                <Button variant="secondary" onClick={handleCloseModalClick}>
                    {t('web_calls_cancel_share_screen')}
                </Button>
                <Button disabled={!selectedSource} onClick={handleStartSharingClick}>
                    {t('web_calls_start_share')}
                </Button>
            </FooterWrapper>
        );
    };

    return (
        <CustomModalLayout
            onClose={handleCloseModalClick}
            isCompactLayout={isShareScreenPermissionNotGranted}
            footer={renderModalFooter()}
            header={
                <>
                    {isShareScreenPermissionNotGranted
                        ? t('web_calls_screen_share_inactive')
                        : t('web_calls_select_share_screen')}
                </>
            }
        >
            {renderModalBody()}
        </CustomModalLayout>
    );
};
